import React, { useEffect } from 'react';
import { useUrlSearchParams } from 'use-url-search-params';
import AppContentView from '@projectK/components/AppContentView';
import generateRoutes from '@projectK/helpers/RouteGenerator';
import { Layouts } from '@projectK/components/AppLayout';
import { useAuthUser } from '@projectK/hooks/AuthHooks'
import AppLoader from '@projectK/components/AppLoader';
import {
  useLayoutActionsContext,
  useLayoutContext,
} from '@projectK/context/LayoutContextProvider';
import { useSidebarActionsContext } from '@projectK/context/SidebarContextProvider';
import {
  anonymousStructure,
  useAuthorizedStructure,
  authorizedStructures,
  useUnAuthorizedStructure,
} from '../AppRoutes';
import { useLocation, useRoutes } from 'react-router-dom';
import { shouldHideAppNavForTournamentRegistration } from '@projectK/modules/ecommerce/TournamentDetail';
import routesConfig from '../AppRoutes/routesConfig';
import WaiverAuthenticate from '../AppRoutes/WaiverAuthenticate';
import jwtAxios from '@projectK/services/auth/JWT';
import { useMobileSettings } from '@projectK/context/MobileContextProvider';
import { useAddonsSettings } from '@projectK/context/CheckAdddons';
import { useRolePermissions } from '@projectK/context/RolesProvider';
import { useMissedPaymentSettings } from '@projectK/context/checkMissedPayments';

const isAppRoute = (isAuthenticated, waiverAuthenticate, path) => {
  if (shouldHideAppNavForTournamentRegistration(path)) {
    return false;
  }
  return (
    isAuthenticated &&
    waiverAuthenticate &&
    path !== '/student/waiver' &&
    path !== '/student/waiver/'
  );
};

const appLayoutView = (
  isAuthenticated,
  waiverAuthenticate,
  path,
  routes,
  routeconfig,
  AppLayouts,
) => {
  return isAppRoute(isAuthenticated, waiverAuthenticate, path) ? (
    <AppLayouts
      routes={routes}
      routesConfig={routeconfig}
      commonRoutes={routeconfig}
    />
  ) : (
    <AppContentView
      routes={routes}
      routesConfig={routeconfig}
      commonRoutes={routeconfig}
    />
  );
};

const AppLayout = () => {
  const { isMobile } = useMobileSettings();

  useEffect(() => {
    if (!isMobile) fetchFavicon();
  }, [isMobile]);

  const fetchFavicon = async () => {
    try {
      const response = await jwtAxios.get(`/public/homelogo/logo`);
      const { data } = response;

      const faviconUrl = data?.setting_value
        ? `${process.env.NX_CLOUD_FRONT_URL}/${data?.setting_value}`
        : '/assets/images/membertraxx.png';

      const favicon = document.getElementById('dynamic-favicon');
      if (favicon) {
        favicon.href = faviconUrl;
      } else {
        const link = document.createElement('link');
        link.id = 'dynamic-favicon';
        link.rel = 'icon';
        link.href = faviconUrl;
        document.head.appendChild(link);
      }
    } catch (error) {
      console.error('Error fetching favicon:', error);
    }
  };

  const { pathname: path } = useLocation();

  const { navStyle } = useLayoutContext();
  const { user, isAuthenticated } = useAuthUser();
  const { updateNavStyle } = useLayoutActionsContext();
  const { updateMenuStyle, setSidebarBgImage } = useSidebarActionsContext();
  const AppLayouts = Layouts[navStyle];
  const { CheckAdddonsSettings, loading } = useAddonsSettings();
  const { checkMissedPayment } = useMissedPaymentSettings();
  const { rolePermissions } = useRolePermissions();
  const [params] = useUrlSearchParams();
  // Wait for addons to finish loading

const unAuthstructureData = useUnAuthorizedStructure();
const authorizedStructureData = useAuthorizedStructure(checkMissedPayment);

const unAuthstructure = unAuthstructureData || authorizedStructures;

const structure = isAuthenticated
  ? authorizedStructureData
  : authorizedStructures;

  const generatedRoutes = generateRoutes({
    isAuthenticated,
    userRole: user?.role,
    unAuthorizedStructure: unAuthstructure,
    authorizedStructure: structure,
    anonymousStructure,
  });

  const routes = useRoutes(generatedRoutes);

  useEffect(() => {
    if (params.layout) updateNavStyle(params.layout);
    if (params.menuStyle) updateMenuStyle(params.menuStyle);
    if (params.sidebarImage) setSidebarBgImage(true);
  }, [
    params.layout,
    params.menuStyle,
    params.sidebarImage,
    params.path,
    updateNavStyle,
    updateMenuStyle,
    setSidebarBgImage,
  ]);
  const routeconfig = routesConfig(user, CheckAdddonsSettings, rolePermissions);
  const waiverAuthenticate = WaiverAuthenticate(user);

  if (loading || !rolePermissions) {
    return <AppLoader />;
  }
  return appLayoutView(
    isAuthenticated,
    waiverAuthenticate,
    path,
    routes,
    routeconfig,
    AppLayouts,
  );
};

export default AppLayout;
